import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDecimalPrice } from '@/lib/commerce-format';
import {
  type CatalogProduct,
  type ProductType,
  useArchiveCatalogProductMutation,
  useCreateCatalogProductMutation,
  useGetCatalogProductQuery,
  useGetCatalogProductsQuery,
  useUpdateCatalogProductMutation,
} from '@/store/api/commerceP2cApi';
import { useToast } from '@/components/shared/Toast';

const productTypes: ProductType[] = [
  'simple', 'variant_parent', 'variant', 'bundle', 'service', 'ticket', 'subscription', 'digital',
];

interface ProductForm {
  name: string;
  slug: string;
  type: ProductType;
  status: 'draft' | 'active';
  categoryId: string;
  description: string;
  unitPrice: string;
  bundlePrice: string;
  currency: string;
}

const emptyForm: ProductForm = {
  name: '', slug: '', type: 'simple', status: 'draft', categoryId: '', description: '',
  unitPrice: '', bundlePrice: '', currency: '',
};

function productBody(form: ProductForm, includeStatus: boolean): Partial<CatalogProduct> & Pick<CatalogProduct, 'name' | 'slug' | 'type'> {
  return {
    name: form.name.trim(),
    slug: form.slug.trim(),
    type: form.type,
    ...(includeStatus ? { status: form.status } : {}),
    ...(form.categoryId.trim() ? { categoryId: form.categoryId.trim() } : {}),
    ...(form.description.trim() ? { description: form.description.trim() } : {}),
    ...(form.unitPrice.trim() ? { unitPrice: form.unitPrice.trim() } : {}),
    ...(form.bundlePrice.trim() ? { bundlePrice: form.bundlePrice.trim() } : {}),
    ...(form.currency.trim() ? { currency: form.currency.trim().toUpperCase() } : {}),
  };
}

export function CatalogPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const createMode = productId === 'new';
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'draft' | 'active' | 'archived'>('all');
  const [cursor, setCursor] = useState<string | undefined>();
  const [rows, setRows] = useState<CatalogProduct[]>([]);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const {
    data: page,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetCatalogProductsQuery({
    cursor,
    limit: 25,
    search: search.trim() || undefined,
    status,
  }, { skip: Boolean(productId) });
  const {
    data: product,
    isLoading: isLoadingProduct,
    isError: isProductError,
    refetch: refetchProduct,
  } = useGetCatalogProductQuery(productId ?? '', { skip: !productId || createMode });
  const [createProduct, { isLoading: isCreating }] = useCreateCatalogProductMutation();
  const [updateProduct, { isLoading: isSaving }] = useUpdateCatalogProductMutation();
  const [archiveProduct, { isLoading: isArchiving }] = useArchiveCatalogProductMutation();
  const busy = isCreating || isSaving;

  useEffect(() => {
    setCursor(undefined);
    setRows([]);
  }, [search, status]);

  useEffect(() => {
    if (!page) return;
    setRows((previous) => {
      if (!cursor) return page.data;
      const seen = new Set(previous.map((row) => row.id));
      return [...previous, ...page.data.filter((row) => !seen.has(row.id))];
    });
  }, [page, cursor]);

  useEffect(() => {
    if (!product) return;
    setForm({
      name: product.name,
      slug: product.slug,
      type: product.type,
      status: product.status === 'archived' ? 'draft' : product.status,
      categoryId: product.categoryId ?? '',
      description: product.description ?? '',
      unitPrice: product.unitPrice ?? '',
      bundlePrice: product.bundlePrice ?? '',
      currency: product.currency ?? '',
    });
  }, [product]);

  async function persist() {
    const body = productBody(form, !createMode);
    try {
      if (createMode) {
        const created = await createProduct({ idempotencyKey: crypto.randomUUID(), body }).unwrap();
        toast.success(`Product “${created.name}” created`);
        navigate(`/catalog/${created.id}`);
      } else if (productId) {
        const updated = await updateProduct({
          id: productId,
          idempotencyKey: crypto.randomUUID(),
          body,
        }).unwrap();
        toast.success(`Product “${updated.name}” saved`);
        setEdit(false);
        setStatusConfirmOpen(false);
      }
    } catch {
      toast.error('Could not save this product. Your changes are still here; try again.');
    }
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError('');
    const body = productBody(form, !createMode);
    if ((body.unitPrice || body.bundlePrice) && !body.currency) {
      setFormError('Currency is required when a unit or bundle price is set.');
      return;
    }
    if (!createMode && product?.status === 'active' && form.status === 'draft') {
      setStatusConfirmOpen(true);
      return;
    }
    await persist();
  }

  async function archive() {
    if (!productId || createMode || !product) return;
    try {
      await archiveProduct({ id: productId, idempotencyKey: crypto.randomUUID() }).unwrap();
      toast.success(`Product “${product.name}” archived`);
      navigate('/catalog');
    } catch {
      toast.error(`Could not archive “${product.name}”. Try again.`);
    }
  }

  if (createMode || productId) {
    const isFormOpen = createMode || edit;
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/catalog')}>← Catalog</Button>
        <header>
          <h1 className="text-2xl font-bold">{createMode ? 'Add catalog product' : product?.name ?? 'Product details'}</h1>
          <p className="text-sm text-muted-foreground">Product prices are decimal strings with an explicit currency.</p>
        </header>
        {isFormOpen ? (
          <form className="max-w-2xl space-y-4" onSubmit={(event) => void save(event)}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Product name" id="product-name">
                <Input id="product-name" required maxLength={200} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
              </Field>
              <Field label="Slug" id="product-slug" help="Lowercase letters, numbers, and hyphens.">
                <Input id="product-slug" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value })} />
              </Field>
              <Field label="Product type" id="product-type">
                <select id="product-type" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value as ProductType, ...(event.target.value === 'variant_parent' ? { unitPrice: '', bundlePrice: '' } : {}) })}>
                  {productTypes.map((type) => <option key={type} value={type}>{type.replaceAll('_', ' ')}</option>)}
                </select>
              </Field>
              {!createMode && (
                <Field label="Availability" id="product-status">
                  <select id="product-status" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as ProductForm['status'] })}>
                    <option value="draft">Draft</option><option value="active">Active</option>
                  </select>
                </Field>
              )}
            </div>
            <Field label="Description" id="product-description">
              <textarea id="product-description" maxLength={10000} className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            </Field>
            {form.type !== 'variant_parent' ? <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Unit price (decimal string)" id="product-unit-price">
                <Input id="product-unit-price" inputMode="decimal" pattern="\d+(?:\.\d{1,4})?" placeholder="e.g. 12.40" value={form.unitPrice} onChange={(event) => setForm({ ...form, unitPrice: event.target.value })} />
              </Field>
              <Field label="Bundle price (decimal string)" id="product-bundle-price">
                <Input id="product-bundle-price" inputMode="decimal" pattern="\d+(?:\.\d{1,4})?" placeholder="e.g. 24.80" value={form.bundlePrice} onChange={(event) => setForm({ ...form, bundlePrice: event.target.value })} />
              </Field>
              <Field label="Currency" id="product-currency" help="Three-letter currency code; required when a price is set.">
                <Input id="product-currency" maxLength={3} pattern="[A-Za-z]{3}" placeholder="EUR" value={form.currency} aria-invalid={Boolean(formError)} aria-describedby={formError ? 'product-form-error' : undefined} onChange={(event) => setForm({ ...form, currency: event.target.value })} />
                {formError && <p id="product-form-error" role="alert" className="text-sm text-destructive">{formError}</p>}
              </Field>
            </div> : <p className="text-sm text-muted-foreground">Variant-parent products do not have a sellable price; prices belong to their variants.</p>}
            <Field label="Category ID" id="product-category">
              <Input id="product-category" value={form.categoryId} onChange={(event) => setForm({ ...form, categoryId: event.target.value })} />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => createMode ? navigate('/catalog') : setEdit(false)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy ? 'Saving…' : createMode ? 'Create product' : 'Save product'}</Button>
            </div>
            {!createMode && product?.status === 'active' && <ConfirmDialog
              open={statusConfirmOpen}
              onOpenChange={setStatusConfirmOpen}
              title="Set active product to draft?"
              description={`“${product.name}” will stop being active in supported commerce flows.`}
              confirmLabel="Set draft"
              variant="destructive"
              isLoading={isSaving}
              onConfirm={() => void persist()}
            />}
          </form>
        ) : isLoadingProduct ? (
          <LoadingRows label="Loading product" columns={3} />
        ) : isProductError || !product ? (
          <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">
            Could not load this product. <Button variant="outline" onClick={() => void refetchProduct()}>Try again</Button>
          </div>
        ) : (
          <section className="max-w-2xl space-y-4">
            <dl className="grid gap-4 border-y py-4 sm:grid-cols-2">
              <Detail label="Product ID" value={product.id} />
              <Detail label="Slug" value={product.slug} />
              <Detail label="Type" value={product.type.replaceAll('_', ' ')} />
              <Detail label="Status" value={product.status} />
              <Detail label="Unit price" value={formatDecimalPrice(product.unitPrice, product.currency)} />
              <Detail label="Bundle price" value={formatDecimalPrice(product.bundlePrice, product.currency)} />
              <Detail label="Category ID" value={product.categoryId ?? '—'} />
              <Detail label="Description" value={product.description ?? '—'} />
            </dl>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={() => setEdit(true)} disabled={product.status === 'archived'}>Edit product</Button>
              {product.status !== 'archived' && <Button variant="destructive" onClick={() => setArchiveOpen(true)}>Archive product</Button>}
            </div>
            <ConfirmDialog
              open={archiveOpen}
              onOpenChange={setArchiveOpen}
              title="Archive product?"
              description={`“${product.name}” will be archived and removed from the default active catalog list.`}
              confirmLabel="Archive product"
              variant="destructive"
              isLoading={isArchiving}
              onConfirm={() => void archive()}
            />
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div><h1 className="text-2xl font-bold">Catalog</h1><p className="text-sm text-muted-foreground">Search products by name or slug.</p></div>
        <Button asChild><Link to="/catalog/new">Add product</Link></Button>
      </header>
      <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
        <Field label="Search catalog" id="catalog-search"><Input id="catalog-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name or slug" /></Field>
        <Field label="Status" id="catalog-status">
          <select id="catalog-status" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
            <option value="all">All statuses</option><option value="draft">Draft</option><option value="active">Active</option><option value="archived">Archived</option>
          </select>
        </Field>
      </div>
      <section aria-busy={isLoading || isFetching}>
        {isLoading && rows.length === 0 ? <LoadingRows label="Loading catalog products" columns={5} />
          : isError && rows.length === 0 ? <Retry onRetry={() => void refetch()} />
            : rows.length === 0 ? <EmptyState
              title={search.trim() || status !== 'all' ? 'No matching products' : 'No products yet'}
              description={search.trim() || status !== 'all' ? 'Change or clear the filters to search again.' : 'Add a product to make it available to supported commerce flows.'}
              action={(search.trim() || status !== 'all') ? <Button variant="outline" onClick={() => { setSearch(''); setStatus('all'); }}>Clear filters</Button> : undefined}
            /> : (
              <div className="space-y-3">
                {isError && <div role="alert" className="text-sm text-destructive">Could not refresh catalog products; loaded rows are retained. <Button variant="outline" onClick={() => void refetch()}>Try again</Button></div>}
                <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="Catalog products">
                  <table className="w-full min-w-[44rem] text-sm">
                    <thead className="bg-muted/50 text-left"><tr>
                      <th scope="col" className="px-4 py-3">Product</th><th scope="col" className="px-4 py-3">Type</th>
                      <th scope="col" className="px-4 py-3 text-right">Price</th><th scope="col" className="px-4 py-3">Status</th>
                    </tr></thead>
                    <tbody>{rows.map((row) => <tr key={row.id} className="border-t">
                      <td className="px-4 py-3"><Link className="font-medium text-primary underline-offset-4 hover:underline" to={`/catalog/${row.id}`}>{row.name}</Link><p className="text-xs text-muted-foreground">{row.slug}</p></td>
                      <td className="px-4 py-3">{row.type.replaceAll('_', ' ')}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatDecimalPrice(row.unitPrice ?? row.bundlePrice, row.currency)}</td>
                      <td className="px-4 py-3">{row.status}</td>
                    </tr>)}</tbody>
                  </table>
                </div>
                {page?.pagination.totalCount !== undefined && <p className="text-sm text-muted-foreground">Showing {rows.length} of {page.pagination.totalCount} products</p>}
                {page?.pagination.hasMore && <Button variant="outline" disabled={isFetching} onClick={() => setCursor(page.pagination.cursor ?? undefined)}>{isFetching ? 'Loading…' : 'Load more products'}</Button>}
              </div>
            )}
      </section>
    </div>
  );
}

function Field({ label, id, help, children }: { label: string; id: string; help?: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label htmlFor={id}>{label}</Label>{children}{help && <p className="text-xs text-muted-foreground">{help}</p>}</div>;
}

function Detail({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-xs text-muted-foreground">{label}</dt><dd className="mt-1 break-words text-sm">{value}</dd></div>;
}

function Retry({ onRetry }: { onRetry: () => void }) {
  return <div role="alert" className="rounded-md border border-destructive/40 p-4 text-sm">Could not load catalog products. <Button variant="outline" onClick={onRetry}>Try again</Button></div>;
}
